import { cloneElement, isValidElement, useId, useState, type InputHTMLAttributes, type ReactElement, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { InfoTip } from './info.js';
import { setThemeMode, useThemeMode } from './theme.js';

export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  className = '',
  type = 'button',
  small,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger' | 'outline';
  disabled?: boolean;
  className?: string;
  type?: 'button' | 'submit';
  small?: boolean;
}) {
  const base = `rounded-2xl font-bold transition active:scale-[0.99] disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
    small ? 'px-3 py-2 text-sm' : 'px-4 py-3.5 text-base'
  }`;
  const look =
    variant === 'primary'
      ? 'bg-brand text-on-brand brand-press shadow-soft'
      : variant === 'danger'
        ? 'bg-danger-soft text-danger'
        : variant === 'outline'
          ? 'border border-line bg-card text-ink hover:bg-card2'
          : 'bg-card2 text-ink hover:bg-line';
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${look} ${className}`}>
      {children}
    </button>
  );
}

export function Spinner() {
  return (
    <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-line border-t-accent align-middle" />
  );
}

export function Card({
  title,
  info,
  children,
  className = '',
  right,
}: {
  title?: ReactNode;
  /** What the card is for, behind an ⓘ beside the title. */
  info?: ReactNode;
  children: ReactNode;
  className?: string;
  right?: ReactNode;
}) {
  return (
    <section className={`rounded-2xl border border-line bg-card shadow-soft ${className}`}>
      {(title || right) && (
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="flex items-center gap-1.5 text-sm font-bold text-ink2">
            {title}
            {info && <InfoTip label={typeof title === 'string' ? title : 'this'}>{info}</InfoTip>}
          </h2>
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * A labelled input. `hint` explains it behind an ⓘ beside the label; `note` stays visible under it
 * (live previews, wording that must be read); `error` replaces the note.
 */
export function Field({
  label,
  hint,
  note,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  note?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  const fallbackId = useId();
  // One input: the label points at it by id, so the ⓘ can sit beside the label without becoming part of it.
  const single = isValidElement<{ id?: string }>(children) ? (children as ReactElement<{ id?: string }>) : null;
  const inputId = single ? (single.props.id ?? fallbackId) : undefined;
  const below = error ? <span className="mt-1 block text-xs text-danger">{error}</span> : note ? <span className="mt-1 block text-xs text-muted">{note}</span> : null;
  if (!single)
    return (
      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted">{label}</span>
        {children}
        {below ?? (hint ? <span className="mt-1 block text-xs text-muted">{hint}</span> : null)}
      </label>
    );
  return (
    <div className="block">
      <div className="mb-1 flex items-center gap-1">
        <label htmlFor={inputId} className="text-xs font-bold uppercase tracking-wide text-muted">{label}</label>
        {hint && <InfoTip label={label}>{hint}</InfoTip>}
      </div>
      {cloneElement(single, { id: inputId })}
      {below}
    </div>
  );
}

export const inputClass =
  'w-full rounded-xl bg-card2 px-4 py-3 text-base text-ink outline-none ring-accent ring-inset placeholder:text-muted focus:ring-2 disabled:opacity-50';

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${inputClass} ${props.className ?? ''}`} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${inputClass} min-h-24 ${props.className ?? ''}`} />;
}

export function Badge({ value, tone }: { value: string; tone?: 'ok' | 'warn' | 'danger' | 'info' | 'muted' }) {
  const look = {
    ok: 'bg-ok-soft text-ok',
    warn: 'bg-warn-soft text-warn',
    danger: 'bg-danger-soft text-danger',
    info: 'bg-info-soft text-info',
    muted: 'bg-line text-ink2',
  }[tone ?? 'muted'];
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-bold ${look}`}>
      {value.replace(/_/g, ' ')}
    </span>
  );
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger' | 'ok'; children: ReactNode }) {
  const look = {
    info: 'bg-info-soft text-info',
    warn: 'bg-warn-soft text-warn',
    danger: 'bg-danger-soft text-danger',
    ok: 'bg-ok-soft text-ok',
  }[tone];
  return <div className={`rounded-2xl px-4 py-3 text-sm ${look}`}>{children}</div>;
}

const MODES = [
  ['auto', 'Auto'],
  ['light', 'Light'],
  ['dark', 'Dark'],
] as const;

/** Appearance picker: follow the device, or force light or dark. */
export function ThemePicker() {
  const mode = useThemeMode();
  return (
    <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold" role="radiogroup" aria-label="Appearance">
      {MODES.map(([value, label]) => (
        <button
          key={value}
          role="radio"
          aria-checked={mode === value}
          onClick={() => setThemeMode(value)}
          className={`flex-1 rounded-full px-3 py-1.5 ${mode === value ? 'bg-card text-ink shadow-card' : 'text-muted'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** Confirm dialog with an optional reason. */
export function Confirm({
  title,
  body,
  confirmLabel = 'Confirm',
  danger,
  withReason,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  withReason?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center">
      <div className="w-full max-w-md rounded-3xl border border-line bg-card p-5 shadow-card">
        <div className="text-lg font-black">{title}</div>
        {body && <div className="mt-2 text-sm text-ink2">{body}</div>}
        {withReason && (
          <TextArea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            className="mt-3"
          />
        )}
        <div className="mt-4 flex gap-2">
          <Button variant="ghost" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={() => onConfirm(reason.trim())} className="flex-1">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
